import React, { useState, useEffect } from 'react';
import { FiBarChart2, FiClock, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';
import UnclassifiedJobs from '../components/UnclassifiedJobs';

function TurnaroundStats() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getAuthToken();
        const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
        // Two different questions: turnaround is calendar days (what the client
        // waits), field-hours is crew hours (what the job costs). Fetched together
        // so they render side by side.
        const [turn, fh] = await Promise.all([
          axios.get(`${API_URL}/analytics/turnaround`, config),
          // Never let the newer panel take the page down with it.
          axios.get(`${API_URL}/analytics/field-hours`, config).catch(() => null),
        ]);
        setData(turn.data);
        if (fh) setHours(fh.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load turnaround stats');
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: '24px' }}>Loading...</div>;
  if (error) return <div style={{ padding: '24px', color: '#dc2626' }}>{error}</div>;
  if (!data) return null;

  const confidenceLabel = (n) => {
    if (n >= 30) return { label: 'Solid', color: '#16a34a' };
    if (n >= 10) return { label: 'Decent', color: '#2563eb' };
    if (n >= 5) return { label: 'Rough', color: '#d97706' };
    return { label: 'Too few', color: '#dc2626' };
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FiBarChart2 /> Turnaround Stats
        </h1>
        <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: '14px' }}>
          How long jobs actually take, based on completed work in the app. Updates automatically as more jobs are paid.
        </p>
      </div>

      <div style={{
        backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px',
        padding: '12px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-start',
      }}>
        <FiInfo color="#0369a1" style={{ marginTop: '2px', flexShrink: 0 }} />
        <div style={{ fontSize: '13px', color: '#0c4a6e' }}>
          <strong>{data.total_samples}</strong> completed jobs analyzed. Calendar days from work-ordered date to completion.
          Imported jobs are excluded. Sample size matters: <strong>5+</strong> = rough estimate, <strong>10+</strong> = decent, <strong>30+</strong> = solid.
        </div>
      </div>

      {/* By Service Type — overall */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '24px',
      }}>
        <h2 style={{ margin: '0 0 14px', fontSize: '17px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiClock /> By Service Type
        </h2>
        {data.by_service.length === 0 ? (
          <div style={{ color: '#6b7280', fontStyle: 'italic', padding: '12px 0' }}>
            No completed jobs yet. As you mark jobs as Paid, this table will fill in.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>Service Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sample Size</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Median Days</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Avg Days</th>
                <th style={{ padding: '8px 12px' }}>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {data.by_service.map((row, i) => {
                const conf = confidenceLabel(row.sample_size);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{row.service_type}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.sample_size}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.median_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{row.avg_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px',
                        backgroundColor: `${conf.color}15`, color: conf.color,
                      }}>{conf.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* By Service Type + Acreage */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <h2 style={{ margin: '0 0 14px', fontSize: '17px' }}>By Service Type + Acreage</h2>
        {data.by_service_and_acreage.length === 0 ? (
          <div style={{ color: '#6b7280', fontStyle: 'italic', padding: '12px 0' }}>
            No data yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>Service Type</th>
                <th style={{ padding: '8px 12px' }}>Acreage</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sample</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Median</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Avg</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Min</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Max</th>
                <th style={{ padding: '8px 12px' }}>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {data.by_service_and_acreage.map((row, i) => {
                const conf = confidenceLabel(row.sample_size);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{row.service_type}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{row.acreage_bucket}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.sample_size}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600' }}>{row.median_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{row.avg_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#9ca3af' }}>{row.min_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#9ca3af' }}>{row.max_days ?? '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px',
                        backgroundColor: `${conf.color}15`, color: conf.color,
                      }}>{conf.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Field hours: what the job COSTS, as opposed to what the client waits ---- */}
      {hours && (
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginTop: '24px',
        }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '17px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiClock /> Field Hours by Service Type
          </h2>
          <p style={{ color: '#6b7280', margin: '0 0 14px', fontSize: '13px' }}>
            Actual crew hours logged on the job — the number that should drive pricing.
            Counts {hours.field_work_types.join(', ')}; office time (drafting, research,
            review) and daily clock entries are excluded.
          </p>

          {hours.by_service.length === 0 ? (
            <div style={{ color: '#6b7280', fontStyle: 'italic', padding: '12px 0' }}>
              No completed field work with a service type yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Service Type</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Jobs</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Median</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Avg</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Range</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '8px 12px' }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {hours.by_service.map((row, i) => {
                  const conf = confidenceLabel(row.sample_size);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{row.service_type}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.sample_size}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>{row.median_hours ?? '—'}h</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{row.avg_hours ?? '—'}h</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#9ca3af' }}>{row.min_hours}–{row.max_hours}h</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{row.total_hours?.toLocaleString()}h</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px',
                          backgroundColor: `${conf.color}15`, color: conf.color,
                        }}>{conf.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* The blind spot, stated plainly. Hiding it would make the table above look
              more authoritative than it has earned. */}
          {hours.unclassified.jobs > 0 && (
            <div style={{
              marginTop: '16px', padding: '12px 16px', borderRadius: '8px',
              backgroundColor: '#fffbeb', border: '1px solid #fde68a',
              fontSize: '13px', color: '#78350f',
            }}>
              <strong>{hours.unclassified.jobs} jobs</strong> carrying{' '}
              <strong>{hours.unclassified.hours.toLocaleString()} field hours</strong> have no
              service type, so they are missing from the table above — that is{' '}
              {Math.round((hours.unclassified.hours / (hours.unclassified.hours + hours.total_field_hours)) * 100)}%
              of all field hours on record. Setting a service type on those jobs makes every
              median above more trustworthy.
              <UnclassifiedJobs />
            </div>
          )}

          {/* Acreage: shown only when there is something real to show. A table of
              "unknown" would look broken and teach nothing. */}
          {(() => {
            // Held to the same bar this page states above: below 5 samples is
            // "Too few", so it has no business being shown as a pricing input.
            // One lonely bucket is worse than none — with today's data a single
            // n=3 row claims 20-100 ac boundaries run FASTER than the overall
            // median, which is an artifact, not a finding.
            const known = (hours.by_service_and_acreage || [])
              .filter((r) => r.acreage_bucket !== 'unknown' && r.sample_size >= 5);
            if (known.length < 2) {
              return (
                <div style={{ marginTop: '16px', fontSize: '13px', color: '#6b7280' }}>
                  <strong style={{ color: '#374151' }}>Acreage breakdown unavailable.</strong>{' '}
                  Almost no jobs have acreage recorded, so hours can't be split by parcel size
                  yet. Acreage comes from the qPublic parcel import — using it routinely on new
                  jobs will fill this in over time.
                </div>
              );
            }
            const src = hours.acreage_sources || {};
            return (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>By Service Type + Acreage</h3>
                <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#6b7280' }}>
                  Acreage on {(src.manual || 0) + (src.quote || 0) + (src.parcel || 0)} of{' '}
                  {(src.manual || 0) + (src.quote || 0) + (src.parcel || 0) + (src.none || 0)} jobs
                  {' — '}
                  <strong>{src.manual || 0} hand-verified</strong>, {src.quote || 0} from quotes,{' '}
                  {src.parcel || 0} from parcel records. Quote and parcel figures can describe the
                  parent tract rather than what was surveyed; correcting them on the job page
                  sharpens these numbers.
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px' }}>Service Type</th>
                      <th style={{ padding: '8px 12px' }}>Acreage</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Jobs</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Median</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {known.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px', fontWeight: '600' }}>{row.service_type}</td>
                        <td style={{ padding: '10px 12px', color: '#6b7280' }}>{row.acreage_bucket}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.sample_size}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>{row.median_hours}h</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#9ca3af' }}>{row.min_hours}–{row.max_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <div style={{ marginTop: '16px', fontSize: '12px', color: '#9ca3af' }}>
            Site conditions recorded on <strong>{hours.conditions.recorded}</strong> of{' '}
            {hours.conditions.eligible} field-complete jobs. Crews began answering at
            close-out on 2026-08-10; this grows as jobs finish.
          </div>
        </div>
      )}
    </div>
  );
}

export default TurnaroundStats;
